import React, { useState, useEffect } from 'react';
import { db } from '../firebase';
import { collection, getDocs, orderBy, query } from 'firebase/firestore';

const CalendarIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
  </svg>
);

const LocationIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
  </svg>
);

const ClockIcon = () => (
  <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
      d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

function EventCard({ event, past }) {
  const [day, month, year] = (() => {
    const d = new Date(event.date + 'T00:00:00');
    return [
      d.getDate(),
      d.toLocaleString('default', { month: 'short' }),
      d.getFullYear(),
    ];
  })();

  return (
    <div className={`bg-white rounded-2xl shadow-sm border overflow-hidden flex flex-col transition-shadow hover:shadow-md ${past ? 'border-gray-100 opacity-80' : 'border-gray-100'}`}>
      {event.imageUrl && (
        <div className="h-48 overflow-hidden">
          <img src={event.imageUrl} alt={event.title}
            className="w-full h-full object-cover" />
        </div>
      )}
      <div className="flex flex-1">
        {/* Date badge */}
        <div className={`flex flex-col items-center justify-center px-5 py-4 min-w-[72px] ${past ? 'bg-gray-100' : 'bg-red-600'}`}>
          <span className={`text-2xl font-bold leading-none ${past ? 'text-gray-500' : 'text-white'}`}>{day}</span>
          <span className={`text-xs font-semibold uppercase tracking-wide mt-0.5 ${past ? 'text-gray-400' : 'text-red-100'}`}>{month}</span>
          <span className={`text-xs mt-0.5 ${past ? 'text-gray-400' : 'text-red-200'}`}>{year}</span>
        </div>
        {/* Content */}
        <div className="flex-1 p-4 flex flex-col justify-between gap-2">
          <div>
            <h3 className="font-semibold text-gray-800 leading-snug">{event.title}</h3>
            {event.description && (
              <p className="text-sm text-gray-500 mt-1 line-clamp-2">{event.description}</p>
            )}
          </div>
          <div className="flex flex-wrap gap-x-4 gap-y-1">
            {event.time && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <ClockIcon />{event.time}
              </span>
            )}
            {event.location && (
              <span className="flex items-center gap-1 text-xs text-gray-400">
                <LocationIcon />{event.location}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetch = async () => {
      try {
        const q = query(collection(db, 'events'), orderBy('date', 'asc'));
        const snap = await getDocs(q);
        setEvents(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, []);

  const today = new Date().toISOString().slice(0, 10);
  const upcoming = events.filter((e) => e.date >= today);
  const past = events.filter((e) => e.date < today).reverse();

  return (
    <div>
      <div className="bg-gray-900 text-white py-20 px-4 text-center">
        <span className="inline-block text-red-400 text-sm font-semibold uppercase tracking-widest mb-4">What's Happening</span>
        <h1 className="text-4xl md:text-6xl font-black mb-4 leading-tight">Events</h1>
        <p className="text-gray-400 text-lg max-w-xl mx-auto">Stay up to date with what Khisa Foundation is doing in the community.</p>
      </div>
      <div className="bg-gray-50 min-h-64">
      <div className="container mx-auto py-16 px-4 max-w-4xl">

        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white rounded-2xl h-28 animate-pulse border border-gray-100" />
            ))}
          </div>
        ) : error ? (
          <p className="text-center text-red-400 py-16">{error}</p>
        ) : events.length === 0 ? (
          <div className="text-center py-20 text-gray-400">
            <CalendarIcon />
            <p className="mt-3">No events scheduled yet. Check back soon.</p>
          </div>
        ) : (
          <div className="space-y-10">
            {upcoming.length > 0 && (
              <section>
                <div className="flex items-center gap-3 mb-4">
                  <h2 className="text-lg font-bold text-gray-800">Upcoming</h2>
                  <span className="bg-red-100 text-red-600 text-xs font-semibold px-2.5 py-0.5 rounded-full">{upcoming.length}</span>
                </div>
                <div className="space-y-3">
                  {upcoming.map((e) => <EventCard key={e.id} event={e} past={false} />)}
                </div>
              </section>
            )}

            {past.length > 0 && (
              <section>
                <h2 className="text-lg font-bold text-gray-600 mb-4">Past Events</h2>
                <div className="space-y-3">
                  {past.map((e) => <EventCard key={e.id} event={e} past />)}
                </div>
              </section>
            )}
          </div>
        )}
      </div>
      </div>
    </div>
  );
}
